// ============================================================
// Datei: frontend/src/pages/LandingPage.tsx
// Landing Page — professionell, Dark Trading Theme
// ============================================================
import React from 'react';
import { useNavigate } from 'react-router-dom';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  const features = [
    { icon: '📊', title: 'Dashboard', desc: 'Real-time metrics: Win Rate, Sharpe Ratio, Drawdown, Equity Curve and more' },
    { icon: '📈', title: 'Trade Tracking', desc: 'Record every trade with PnL, R:R, tags, custom fields and detailed notes' },
    { icon: '🏆', title: 'S&P 500 Benchmark', desc: 'Real Yahoo Finance data — compare your portfolio with the market' },
    { icon: '🤖', title: 'ML-Analyse', desc: 'AI analyzes your patterns and provides optimization suggestions' },
    { icon: '📓', title: 'Trading Journal', desc: 'Document your thoughts, strategies and emotions for every trade' },
    { icon: '📅', title: 'Kalender Heatmap', desc: 'GitHub-style heatmap shows your profitable and losing days at a glance' },
    { icon: '📊', title: 'CSV Import/Export', desc: 'Import trades from Excel or export everything for external analysis' },
    { icon: '📱', title: 'Mobile Responsive', desc: 'Perfectly usable on smartphone, tablet and desktop' },
  ];

  const stats = [
    { value: '15+', label: 'Metrics' },
    { value: 'Live', label: 'S&P 500 Data' },
    { value: '100%', label: 'Free' },
    { value: '🔒', label: 'Encrypted' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#0a0e1a', color: '#e2e8f0', overflowX: 'hidden' }}>

      {/* Navbar */}
      <nav style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '18px 40px', borderBottom: '1px solid #1e293b22',
        position: 'sticky', top: 0, background: '#0a0e1aee', backdropFilter: 'blur(20px)', zIndex: 100,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 28 }}>📊</span>
          <span style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>Trading Engine</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#8b5cf6', background: '#8b5cf622', padding: '2px 8px', borderRadius: 8 }}>v2.2</span>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={() => navigate('/login')}
            style={{ background: 'none', border: '1px solid #334155', color: '#94a3b8', padding: '10px 22px', borderRadius: 10, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
            Log in
          </button>
          <button onClick={() => navigate('/login')}
            style={{ background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', border: 'none', color: '#fff', padding: '10px 22px', borderRadius: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
            Start for free
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section style={{
        textAlign: 'center', padding: '100px 20px 80px', position: 'relative',
        background: 'radial-gradient(ellipse at center top, rgba(139,92,246,0.12) 0%, transparent 60%)',
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#8b5cf6', marginBottom: 16, letterSpacing: 2, textTransform: 'uppercase' }}>
          Trading Analytics Platform
        </div>
        <h1 style={{ fontSize: 'clamp(36px, 5vw, 64px)', fontWeight: 800, lineHeight: 1.1, marginBottom: 24, maxWidth: 800, margin: '0 auto 24px' }}>
          Your Trading.{' '}
          <span style={{ background: 'linear-gradient(135deg, #8b5cf6, #ec4899)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Professionally analyzed.
          </span>
        </h1>
        <p style={{ fontSize: 18, color: '#94a3b8', maxWidth: 600, margin: '0 auto 40px', lineHeight: 1.6 }}>
          Track your trades, analyze your performance, benchmark against the S&P 500 —
          all in one platform. Powered by Rust, React & Machine Learning.
        </p>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/login')}
            style={{
              background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', border: 'none', color: '#fff',
              padding: '16px 36px', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 17,
              boxShadow: '0 8px 32px rgba(139,92,246,0.4)',
            }}>
            🚀 Start for free now
          </button>
          <button onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
            style={{
              background: '#1e293b', border: '1px solid #334155', color: '#e2e8f0',
              padding: '16px 36px', borderRadius: 12, cursor: 'pointer', fontWeight: 600, fontSize: 17,
            }}>
            View features ↓
          </button>
        </div>

        {/* Stats Row */}
        <div style={{
          display: 'flex', justifyContent: 'center', gap: 48, marginTop: 70, flexWrap: 'wrap',
        }}>
          {stats.map((s, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 32, fontWeight: 800, color: '#fff' }}>{s.value}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Dashboard Preview */}
      <section style={{ padding: '0 40px 80px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{
          background: '#111827', border: '1px solid #1e293b', borderRadius: 20,
          padding: 6, boxShadow: '0 20px 80px rgba(0,0,0,0.5)',
        }}>
          <div style={{
            background: '#0f172a', borderRadius: 16, padding: '40px 30px',
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16,
          }}>
            {[
              ['TRADES', '247'],
              ['WIN RATE', '68.4%'],
              ['SHARPE', '2.31'],
              ['NET PnL', '+$12,840'],
              ['ALPHA', '+14.2%'],
              ['MAX DD', '4.8%'],
            ].map(([label, value], i) => (
              <div key={i} style={{
                background: '#13172b', border: '1px solid #1e2539', borderRadius: 12, padding: 16,
              }}>
                <div style={{ color: '#64748b', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
                <div style={{ color: i === 3 || i === 4 ? '#22c55e' : '#e2e8f0', fontSize: 22, fontWeight: 700, marginTop: 6 }}>{value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" style={{ padding: '80px 40px', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#8b5cf6', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>Features</div>
          <h2 style={{ fontSize: 36, fontWeight: 800, color: '#fff', marginBottom: 14 }}>
            Alles was du brauchst
          </h2>
          <p style={{ color: '#94a3b8', fontSize: 16, maxWidth: 500, margin: '0 auto' }}>
            Professional trading analysis without the cost of expensive software
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20 }}>
          {features.map((f, i) => (
            <div key={i} style={{
              background: '#111827', border: '1px solid #1e293b', borderRadius: 16, padding: '28px 24px',
              transition: 'border-color 0.3s, transform 0.2s',
              cursor: 'default',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#8b5cf644'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#1e293b'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ fontSize: 36, marginBottom: 14 }}>{f.icon}</div>
              <h3 style={{ color: '#f1f5f9', fontSize: 18, fontWeight: 700, marginBottom: 8 }}>{f.title}</h3>
              <p style={{ color: '#94a3b8', fontSize: 14, lineHeight: 1.6 }}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benchmark Section */}
      <section style={{
        padding: '80px 40px',
        background: 'linear-gradient(180deg, transparent 0%, rgba(139,92,246,0.05) 50%, transparent 100%)',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60, alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 12 }}>Live Benchmark</div>
            <h2 style={{ fontSize: 32, fontWeight: 800, color: '#fff', marginBottom: 16 }}>
              Are you beating the S&P 500?
            </h2>
            <p style={{ color: '#94a3b8', fontSize: 16, lineHeight: 1.7, marginBottom: 24 }}>
              Real market data from Yahoo Finance — no fake, no mock.
              Automatically compare your trading returns with a buy-and-hold strategy in the S&P 500.
            </p>
            <div style={{ display: 'flex', gap: 20 }}>
              <div style={{ background: '#1e293b', borderRadius: 12, padding: '16px 20px', textAlign: 'center' }}>
                <div style={{ color: '#f59e0b', fontSize: 24, fontWeight: 800 }}>$6,836</div>
                <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>S&P 500 Live</div>
              </div>
              <div style={{ background: '#1e293b', borderRadius: 12, padding: '16px 20px', textAlign: 'center' }}>
                <div style={{ color: '#22c55e', fontSize: 24, fontWeight: 800 }}>+18.4%</div>
                <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>1J Return</div>
              </div>
            </div>
          </div>
          <div style={{
            background: '#111827', border: '1px solid #1e293b', borderRadius: 16, padding: 24,
          }}>
            {/* Simulated chart */}
            <div style={{ color: '#64748b', fontSize: 12, marginBottom: 12 }}>📊 Equity Comparison — Portfolio vs S&P 500</div>
            <svg viewBox="0 0 400 200" style={{ width: '100%' }}>
              {/* Grid */}
              {[50, 100, 150].map(y => (
                <line key={y} x1="0" y1={y} x2="400" y2={y} stroke="#1e293b" strokeWidth="1" />
              ))}
              {/* S&P 500 line */}
              <polyline
                points="0,140 50,135 100,130 150,125 200,115 250,110 300,100 350,95 400,90"
                fill="none" stroke="#f59e0b" strokeWidth="2" strokeDasharray="6 3" opacity="0.7"
              />
              {/* User portfolio line */}
              <polyline
                points="0,140 50,120 100,110 150,130 200,90 250,85 300,70 350,65 400,50"
                fill="none" stroke="#8b5cf6" strokeWidth="3"
              />
              {/* Gradient fill */}
              <defs>
                <linearGradient id="pfill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity="0.2" />
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0" />
                </linearGradient>
              </defs>
              <polygon
                points="0,140 50,120 100,110 150,130 200,90 250,85 300,70 350,65 400,50 400,200 0,200"
                fill="url(#pfill)"
              />
            </svg>
            <div style={{ display: 'flex', gap: 20, marginTop: 10, fontSize: 12 }}>
              <span style={{ color: '#8b5cf6' }}>━ Your Portfolio</span>
              <span style={{ color: '#f59e0b' }}>╴╴ S&P 500</span>
            </div>
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section style={{ padding: '60px 40px', maxWidth: 1100, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 24 }}>Powered by</div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap' }}>
          {['🦀 Rust', '⚛️ React', '🐘 PostgreSQL', '📊 Recharts', '🤖 ML', '📈 Yahoo Finance'].map((t, i) => (
            <div key={i} style={{ color: '#94a3b8', fontSize: 16, fontWeight: 600, padding: '10px 20px', background: '#111827', borderRadius: 10, border: '1px solid #1e293b' }}>
              {t}
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '80px 40px', textAlign: 'center' }}>
        <div style={{
          maxWidth: 700, margin: '0 auto', background: 'linear-gradient(135deg, #1e293b, #0f172a)',
          border: '1px solid #8b5cf644', borderRadius: 24, padding: '60px 40px',
        }}>
          <h2 style={{ fontSize: 32, fontWeight: 800, color: '#fff', marginBottom: 14 }}>
            Ready to take your trading to the next level?
          </h2>
          <p style={{ color: '#94a3b8', fontSize: 16, marginBottom: 32 }}>
            Register for free. No credit card required.
          </p>
          <button onClick={() => navigate('/login')}
            style={{
              background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)', border: 'none', color: '#fff',
              padding: '16px 48px', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: 18,
              boxShadow: '0 8px 32px rgba(139,92,246,0.4)',
            }}>
            🚀 Start for free
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer style={{ padding: '30px 40px', borderTop: '1px solid #1e293b', textAlign: 'center' }}>
        <div style={{ color: '#475569', fontSize: 13 }}>
          Trading Engine v2.2 — Built with 🦀 Rust + ⚛️ React + 💜 by a trader for traders
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;